import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { productService } from "../services/productService";
import { profilerService } from "../services/profilerService";
import {
  normalizeNicheCategoryState,
  normalizeProductsResponse,
} from "../utils/productManagement";
import {
  AlertCircle,
  ArrowRight,
  BadgeCheck,
  Loader2,
  MapPin,
  Package,
  ShieldAlert,
  ShoppingBag,
  Store,
} from "lucide-react";

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const sortByUpdatedAt = (left, right) => {
  const leftTime = Date.parse(left?.updatedAt || left?.createdAt || "") || 0;
  const rightTime = Date.parse(right?.updatedAt || right?.createdAt || "") || 0;
  return rightTime - leftTime;
};

export default function Overview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = user?.token;
  const hasSellerAccount = Boolean(user?.seller?.id || user?.sellerAccountId);

  const profileQuery = useQuery({
    queryKey: ["profiler-account-me"],
    queryFn: async () => profilerService.getMe(token),
    enabled: Boolean(token) && hasSellerAccount,
  });

  const nicheQuery = useQuery({
    queryKey: ["my-niche-categories"],
    queryFn: async () =>
      normalizeNicheCategoryState(await productService.getMyNicheCategories()),
    enabled: Boolean(token) && hasSellerAccount,
  });

  const productsQuery = useQuery({
    queryKey: ["seller-products", "overview"],
    queryFn: async () =>
      normalizeProductsResponse(
        await productService.listMyProducts({ sortBy: "latest" }),
      ),
    enabled: Boolean(token) && hasSellerAccount,
  });

  const seller = profileQuery.data?.seller || user?.seller || null;
  const plan = seller?.plan || null;
  const market = seller?.market || null;
  const nicheState = nicheQuery.data || normalizeNicheCategoryState({});
  const products = useMemo(() => productsQuery.data || [], [productsQuery.data]);

  const recentProducts = useMemo(
    () => [...products].sort(sortByUpdatedAt).slice(0, 4),
    [products],
  );

  const loading =
    Boolean(token) &&
    hasSellerAccount &&
    (profileQuery.isLoading || nicheQuery.isLoading || productsQuery.isLoading);

  const loadError = profileQuery.error
    ? getApiErrorMessage(profileQuery.error, "Could not load seller overview.")
    : nicheQuery.error
      ? getApiErrorMessage(
          nicheQuery.error,
          "Could not load approved niche categories.",
        )
      : productsQuery.error
        ? getApiErrorMessage(productsQuery.error, "Could not load products.")
        : "";

  if (!hasSellerAccount) {
    return (
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-10 text-center dark:border-gray-700 dark:bg-gray-900">
          <Store size={40} className="mx-auto text-gray-300 dark:text-gray-700" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
            Seller account not provisioned yet
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            The current seller contract exposes overview data only after
            provisioning. Complete seller onboarding from the tenant dashboard,
            then return here when your seller account is active.
          </p>
          <button
            type="button"
            onClick={() => navigate("/tenant")}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500">
            Open tenant dashboard <ArrowRight size={15} />
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="mx-auto animate-spin text-violet-500" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Loading seller overview...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <section className="rounded-3xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-violet-500">
              Seller Dashboard
            </p>
            <h2 className="mt-2 text-3xl font-black text-gray-900 dark:text-gray-100">
              {seller?.shopName || user?.shopName || "Seller account"}
            </h2>
            <p className="mt-2 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
              This overview stays grounded in backend-backed seller state only:
              seller profile, current plan, approved niche categories, and your
              product listings. Sales analytics are not exposed by the current
              seller contract, so they are intentionally omitted here.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {user?.verified && (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
                <BadgeCheck size={13} /> Verified seller
              </span>
            )}
            <button
              type="button"
              onClick={() => navigate("/dashboard/products")}
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500">
              Manage products <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>

      {seller?.isBanned && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
          <div className="flex items-start gap-3">
            <ShieldAlert size={18} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Seller restrictions are active</p>
              <p className="mt-1">
                {seller?.banReason ||
                  "Product and category changes remain blocked until this restriction is removed."}
              </p>
            </div>
          </div>
        </div>
      )}

      {loadError && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Overview data is incomplete</p>
              <p className="mt-1">{loadError}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="Products"
          value={String(products.length)}
          hint={
            plan?.maxProducts || nicheState.maxProducts
              ? `${products.length}/${plan?.maxProducts || nicheState.maxProducts} used`
              : "Plan cap unavailable"
          }
          icon={Package}
        />
        <SummaryCard
          label="Approved Categories"
          value={String(nicheState.selectedCategories.length)}
          hint={
            plan?.maxCategories || nicheState.maxCategories
              ? `${nicheState.selectedCategories.length}/${plan?.maxCategories || nicheState.maxCategories} selected`
              : "No category cap returned"
          }
          icon={ShoppingBag}
        />
        <SummaryCard
          label="Current Market"
          value={market?.name || "Not assigned"}
          hint={market?.city || "Market assignment pending"}
          icon={MapPin}
        />
        <SummaryCard
          label="Seller Status"
          value={seller?.isBanned ? "Restricted" : "Active"}
          hint={plan?.name || user?.planName || user?.plan || "No plan resolved"}
          icon={Store}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.2fr_0.8fr]">
        <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Account summary
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Provisioned seller identity, location, and current plan details.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/dashboard/profile")}
              className="text-sm font-semibold text-violet-600 transition hover:text-violet-500 dark:text-violet-300 dark:hover:text-violet-200">
              Edit profile
            </button>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <InfoCard label="Shop Name" value={seller?.shopName || "Not set"} />
            <InfoCard
              label="Store Address"
              value={seller?.storeAddress || "Not set"}
            />
            <InfoCard label="Phone" value={user?.phone || user?.phoneNumber || "-"} />
            <InfoCard
              label="Plan"
              value={plan?.name || user?.planName || user?.plan || "Not resolved"}
            />
          </div>

          <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
              Approved Categories
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {nicheState.selectedCategories.length > 0 ? (
                nicheState.selectedCategories.map((category) => (
                  <span
                    key={category.id}
                    className="rounded-full bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-700 dark:bg-violet-500/10 dark:text-violet-200">
                    {category.name}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No approved niche categories have been selected yet.
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Recent Listings
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Your most recently updated product listings.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/dashboard/products")}
              className="text-sm font-semibold text-violet-600 transition hover:text-violet-500 dark:text-violet-300 dark:hover:text-violet-200">
              View all
            </button>
          </div>

          <div className="mt-5 space-y-3">
            {recentProducts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-700">
                <Package size={32} className="mx-auto text-gray-300 dark:text-gray-700" />
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  No products have been created yet.
                </p>
              </div>
            ) : (
              recentProducts.map((product) => (
                <article
                  key={product.id}
                  className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {product.name}
                      </p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {product.categoryName} · Updated {formatDateTime(product.updatedAt)}
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      Qty {product.quantity}
                    </span>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, hint, icon: Icon }) {
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
            {label}
          </p>
          <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-gray-100">
            {value}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{hint}</p>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300">
          <Icon size={18} />
        </div>
      </div>
    </article>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        {value}
      </p>
    </div>
  );
}
